import React, { useMemo, useRef } from 'react'
import { Col, Form } from 'antd'
import { Formik } from 'formik'
import produce from 'immer'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { Button } from 'src/components/Ant'
import {
  getInitialValues,
  getValidationSchema
} from 'src/core/forms/request/billing'
import { RequestStandardBilling } from 'src/types'
import { useStepsContext } from 'src/views/Requests/views/Form'
import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'
import useStandardResource from 'src/core/hooks/use-standard-resource'

import { WrapButtons } from './styles'
import BillingSummary from './components/BillingSummary'
import FormFields from './components/FormFields'

import { WrapCard, WrapRow } from '../../../styles'
import SegmentControl from '../SegmentControl'
import StepsNavigation from '../../../../StepsNavigation'

interface Params {
  reason_id: string
}

const initialValues = getInitialValues()

function Billing({
  match: {
    params: { reason_id }
  }
}: RouteComponentProps<Params>) {
  const billingTypeStandard = useStandardResource('billing_types')
  const targetStandard = useStandardResource('targets')

  const stepsContext = useStepsContext()

  const policyRule = useReasonPolicyRule(reason_id)

  const refFormik = useRef(null)

  function handleResetForm() {
    setTimeout(() => {
      refFormik.current.resetForm({
        ...initialValues,
        billing_type: billingTypeStandard,
        billing_type_id: billingTypeStandard
          ? billingTypeStandard.billing_type_id.toString()
          : null,
        target: targetStandard,
        target_id: targetStandard ? targetStandard.target_id.toString() : null
      })
    }, 100)
  }

  const hasBilling = useMemo(
    () =>
      stepsContext?.request?.request_standard_assignments
        .request_standard_billings.length > 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepsContext.request]
  )
  return (
    <Formik
      ref={refFormik}
      initialValues={{ ...initialValues }}
      onSubmit={values => {
        stepsContext.setRequest(
          produce(stepsContext.request, draft => {
            if (values.request_standard_billing_id || values.tmp_id) {
              draft.request_standard_assignments.request_standard_billings =
                draft.request_standard_assignments.request_standard_billings.map(
                  editedItem => ({
                    ...editedItem,
                    ...(editedItem.tmp_id === values.tmp_id &&
                    editedItem.request_standard_billing_id ===
                      values.request_standard_billing_id
                      ? values
                      : {})
                  })
                )
            } else {
              draft.request_standard_assignments.request_standard_billings.push(
                {
                  ...values,
                  ...(values.request_standard_billing_id
                    ? {}
                    : { tmp_id: Math.random() })
                }
              )
            }
          })
        )

        handleResetForm()
      }}
      validationSchema={getValidationSchema({ policyRule })}
      render={formikProps => {
        const isTempId =
          !!formikProps.values.request_standard_billing_id ||
          !!formikProps.values.tmp_id

        return (
          <Form onSubmit={formikProps.handleSubmit}>
            <StepsNavigation />
            <WrapRow>
              <Col span={15}>
                <WrapCard title={<SegmentControl />}>
                  <FormFields
                    enable_billing_observation={
                      policyRule?.enable_billing_observation
                    }
                    formikProps={formikProps}
                    organization_id={stepsContext.request.organization_id}
                  />
                  <Form.Item>
                    <WrapButtons>
                      <div>
                        <Button
                          block
                          data-test-id="billing-action"
                          disabled={!isTempId && hasBilling}
                          htmlType="submit"
                          icon={isTempId ? 'edit' : ''}
                          title={
                            isTempId
                              ? 'common.actions.change'
                              : 'common.actions.save'
                          }
                          type="primary"
                        />
                      </div>
                      {isTempId && (
                        <div>
                          <Button
                            onClick={() => handleResetForm()}
                            type="dashed"
                            title="common.actions.cancel-edit"
                          />
                        </div>
                      )}
                    </WrapButtons>
                  </Form.Item>
                </WrapCard>
              </Col>
              <Col offset={1} span={8}>
                <BillingSummary
                  bodyStyle={{
                    maxHeight: 400,
                    minHeight: 320,
                    overflow: 'auto'
                  }}
                  handleDelete={(itemDelete: RequestStandardBilling) => {
                    stepsContext.setRequest(
                      produce(stepsContext.request, draft => {
                        if (itemDelete.request_standard_billing_id) {
                          draft.request_standard_assignments.request_standard_billings.forEach(
                            deletedItem =>
                              (deletedItem.active =
                                deletedItem.request_standard_billing_id ===
                                itemDelete.request_standard_billing_id
                                  ? false
                                  : itemDelete.active)
                          )
                        } else if (itemDelete.tmp_id) {
                          draft.request_standard_assignments.request_standard_billings =
                            draft.request_standard_assignments.request_standard_billings.filter(
                              deletedItem =>
                                deletedItem.tmp_id !== itemDelete.tmp_id
                            )
                        }
                      })
                    )

                    handleResetForm()
                  }}
                  handleEdit={(itemEdit: RequestStandardBilling) =>
                    formikProps.resetForm(itemEdit)
                  }
                  request={stepsContext.request}
                />
              </Col>
            </WrapRow>
          </Form>
        )
      }}
    />
  )
}

export default withRouter(Billing)
